import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axiosOrg, { AxiosError, AxiosResponse } from "axios";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { LazyLoadImage } from "react-lazy-load-image-component";

//custom components
import Title from "../../../components/common/Title/Title.component";
import Breadcrumb from "../../../components/common/Breadcrumb/Breadcrumb.component";
import FilePreview from "../../../components/common/FilePreview/FilePreview.component";

//hooks
import { usePageError } from "../../../hooks/hooks";

//utils
import axios from "../../../utils/api";
import { bgHeaders, dragActive } from "../../../styles/theme";

const ItemsWrapper = styled.div`
  margin-top: calc(5 * var(--atom));
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  justify-content: center;

  &.items-2 {
    justify-content: center;
    .item {
      width: 48%;
    }
  }

  .item {
    width: 31.3333333%;
    // height: calc(31 * var(--atom));
    margin: 0 1% 2%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-direction: column;
    cursor: pointer;

    .inner-content {
      box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
      border-radius: calc(1.6 * var(--atom));
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      align-items: center;
      width: 100%;
      height: 100%;
      background-color: ${bgHeaders};
      min-height: calc(36.6 * var(--atom));
      // max-height: calc(36.6 * var(--atom));
      > span {
        display: flex !important;
        align-items: center;
        justify-content: center;
        width: calc(14 * var(--atom));
        height: calc(14 * var(--atom));
        font-size: calc(3.4 * var(--atom));
        padding: var(--atom) 0;

        &.title {
          height: auto;
          width: 100%;
          padding: var(--atom);
          color: #234182;
        }

        img {
          max-width: calc(14 * var(--atom));
          max-height: calc(14 * var(--atom));
        }
      }
    }

    .title {
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      text-align: center;
    }
    &:active {
      .inner-content {
        // background: ${dragActive}
      }
    }
  }
  @media (max-width: 640px) {
    .item {
      width: 46%;
    }
  }
  @media (max-width: 480px) {
    .item {
      width: 100%;
    }
  }
`;

const PageWrapper = styled.div`
  h1 {
    font-size: 28px;
    line-height: 38px;
    text-align: center;
    margin: 0;
  }
`;

interface Props {}

const PageEntity: React.FC<Props> = () => {
  const { id, folderId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { setVisibleError, setRetryFn, setErrorMsg } = usePageError();
  const [entityData, setEntityData] = useState<any>(null);
  const [entityTitle, setEntityTitle] = useState<string>("");
  const [file, setFile] = useState<any>(null);

  //get entity data
  const { refetch: getEntityData } = useQuery(
    "getEntityData",
    async () => {
      // setVisible(false);
      const data = await axios(
        `folders?include=image&filter[category_id]=${id}&filter[folder_id]=${
          folderId || "null"
        }&sort=order`,
        {
          headers: {
            "Accept-Language": i18n.language,
          },
        }
      );
      return data;
    },
    {
      onSuccess: (data: AxiosResponse<any>) => {
        if (!axiosOrg.isAxiosError(data)) {
          setEntityData(data?.data);
          setEntityTitle(data?.data.path?.[(data?.data?.path?.length || 1) - 1]?.name);
        }
      },
      onError: (e: AxiosError<any>) => {
        setErrorMsg(e.response?.data?.message || `${t("common.errorGettingData")}`);
        setVisibleError(true);
        setRetryFn({
          execute: getEntityData,
        });
      },
    }
  );

  const pageClass = () => {
    if (Array.isArray(entityData?.data) && entityData?.data.length === 2) {
      return "items-2";
    }
    return "";
  };

  useEffect(() => {
    getEntityData();
    setFile(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, i18n.language]);

  return (
    <PageWrapper>
      <Breadcrumb
        data={
          Array.isArray(entityData?.path)
            ? entityData?.path?.map((item: any) => ({
                id: item.id,
                name: item.name,
              }))
            : []
        }
      />
      <h1>{entityTitle}</h1>
      <ItemsWrapper className={pageClass()}>
        {Array.isArray(entityData?.data) && entityData?.data?.length > 0 ? (
          entityData?.data?.map((entity: any) => (
            <div
              className="item"
              key={entity.id}
              onClick={() => {
                if (entity?.type === "file") {
                  navigate(`/file/${entity.id}`);
                } else {
                  navigate(`/categories/${id}/${entity?.id}`);
                }
              }}
            >
              <div className="inner-content">
                <LazyLoadImage
                  className="img-wrapper"
                  alt={entity.image?.alt}
                  src={entity.image?.path}
                  effect="opacity"
                />
                <span className="title">{entity?.name}</span>
              </div>
            </div>
          ))
        ) : (
          <span style={{ marginLeft: "calc(2 * var(--atom))" }}>{t("common.noItems")}</span>
        )}
      </ItemsWrapper>
      {file && (
        <FilePreview
          file={file}
          closeFn={() => setFile(null)}
          groupTitle={file.name}
        />
      )}
    </PageWrapper>
  );
};

export default PageEntity;
